import { createReplicaRemote } from '@w/db-replication/server'
import { flashcardsReplicationBudgets } from '../db/replication-budgets'
import { nativeBootstrapCatalog } from '../server/replication/page-security-catalog'
import { personalRootTransaction } from '../server/replication/personal-root'
import { ulid } from '@w/utils'

type Fetch = Window['fetch']
type PrincipalSource = () => { id: string; anonymous?: true } | null

/** flashcards-local-first.spec.md "Transport outcomes": user-initiated login requests reach the network while anonymous. */
const anonymousNetworkPaths = /\/auth\/(?:magic-link\/(?:send|verify)|google\/|telegram\/)/
/** The `/tel` export: `/tel` and its sub-paths (`/traces`, `/metrics`, `/error-groups`). */
export const telemetryPath = /\/tel(?:\/|$)/

/**
 * flashcards-local-first.spec.md PrincipalFetch: the one boundary between the app and the backend origin. An account
 * principal uses the network as before. The anonymous principal reaches the network only for an explicit login (with
 * the caller's credentials) and the `/tel` export (credentials omitted); the
 * local backend answers the replica's first download with the log a fresh personal workspace has on the server (the
 * bundled catalog, then its root and grant; generation 1), and every
 * other request fails as offline (`TypeError`), the outcome the app already handles for an account without network.
 * The local backend never answers a status >= 400, so no 403/410 reset or purge branch runs for an anonymous replica.
 */
export function createPrincipalFetch(options: { network: Fetch; principal: PrincipalSource; storage: Storage; now?: () => number }): Fetch {
  const backends = new Map<string, ReturnType<typeof createLocalBackend>>()
  return (input, init) => {
    const principal = options.principal()
    if (!principal?.anonymous) return options.network(input, init)
    const request = new Request(input, init)
    const path = new URL(request.url).pathname
    // The telemetry export (`/tel` and its sub-paths, http-exporters.ts) reaches the network without credentials: the
    // anonymous principal has no session to send.
    if (telemetryPath.test(path)) return options.network(new Request(request, { credentials: 'omit' }))
    if (anonymousNetworkPaths.test(path)) return options.network(request)
    let local = backends.get(principal.id)
    if (!local) backends.set(principal.id, local = createLocalBackend(principal.id, anonymousPersonalRootId(options.storage, principal.id), options.now?.() ?? Date.now()))
    return local(request)
  }
}

/** The server's log for a fresh personal workspace: the catalog, then the root and the owner's grant (revision catalog+1). */
function createLocalBackend(principalId: string, rootId: string, now: number) {
  const log = [...nativeBootstrapCatalog.map(entry => entry.transaction), personalRootTransaction(rootId, principalId, now)]
  const head = BigInt(log.length)
  const transactions = log.map((bytes, index) => ({ revision: BigInt(index + 1), bytes }))
  const remote = createReplicaRemote({
    append: () => Promise.reject(new Error('INV: the local backend never appends')),
    read: ({ cursor }) => Promise.resolve({ since: cursor, head, transactions: transactions.filter(entry => entry.revision > cursor) }),
  }, { requestBytes: flashcardsReplicationBudgets.requestBytes, responseBytes: 8 * 1024 * 1024 })
  return async (request: Request): Promise<Response> => {
    const operation = /\/replica\/(admit|fetch)$/.exec(new URL(request.url).pathname)?.[1]
    if (operation === 'admit') return Response.json({ generation: 1 })
    if (operation === 'fetch') {
      const bytes = await remote.fetchRemote(new Uint8Array(await request.arrayBuffer()))
      return new Response(new Uint8Array(bytes), { headers: { 'content-type': 'application/octet-stream', 'x-replica-generation': '1' } })
    }
    throw new TypeError('Offline: the anonymous principal has no backend')
  }
}

/**
 * The anonymous principal's directory and permission levels, held where an offline account finds its last answer
 * (flashcards-rpc-options.ts retained `workspace.list` / `page.levels`): one personal workspace the principal owns, Full on
 * its personal root. Written once, before the first directory read.
 */
export function seedAnonymousDirectory(storage: Storage, principal: { id: string; anonymous?: true }) {
  if (!principal.anonymous) return
  const { id } = principal
  const listKey = anonymousListKey(id)
  if (storage.getItem(listKey) !== null) return
  // Its personal root id is minted here, once, like the server mints an account's (db-crdt-sync.spec.md root-pages-have-ulid-ids).
  const personalRootId = ulid()
  storage.setItem(JSON.stringify(['workspace-security', id, 'page.levels', { workspaceId: id }]), JSON.stringify({ securityRevision: 0, levels: { [personalRootId]: 3 } }))
  storage.setItem(listKey, JSON.stringify({ workspaces: [{ id, name: 'Personal', updatedAt: new Date(0).toISOString(), personalRootId }],
    memberships: [{ id, userId: id, workspaceId: id, role: 'admin', email: '', name: null, avatar: null }], invites: [] }))
}

function anonymousListKey(principalId: string) { return JSON.stringify(['workspace-security', principalId, 'workspace.list', undefined]) }
/** The anonymous principal's personal root id, as its seeded directory holds it. */
export function anonymousPersonalRootId(storage: Storage, principalId: string): string {
  const saved = storage.getItem(anonymousListKey(principalId))
  const entry = saved === null ? undefined : (JSON.parse(saved) as { workspaces: { id: string; personalRootId?: string }[] }).workspaces.find(workspace => workspace.id === principalId)
  if (!entry?.personalRootId) throw new Error('INV: the anonymous directory is seeded before its local backend serves the root')
  return entry.personalRootId
}
